import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Archive, ArchiveRestore, Bell, Calendar, CheckCircle2, ChevronDown, ChevronRight, Clock, ExternalLink, Layers, Lock, Pause, Play, RefreshCw, Route, SkipForward, Trash2, Undo2, AlignLeft } from 'lucide-react'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ActionMenu } from '../components/ui/ActionMenu'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { Timestamp } from '../components/ui/Timestamp'
import { LifeAreasPicker } from '../components/ui/LifeAreasPicker'
import { useToast } from '../components/ui/Toast'
import { HabitService, type Habit, type HabitBoardCard, type HabitJourneyPlan } from '../services/habitService'
import { habitLifecycle, type PausePreset } from '../services/habitLifecycleService'
import { useHabitAdjustmentCheckinForHabit } from '../hooks/useHabitAdjustmentCheckin'
import { HabitAdjustmentCheckinCard } from '../components/habits/HabitAdjustmentCheckinCard'
import { HabitKeepingUp } from '../components/habits/HabitKeepingUp'
import { HabitSteps } from '../components/habits/HabitSteps'
import { HabitGapsCard } from '../components/habits/HabitGapsCard'
import {
  HabitAddRow, HabitBareMinimumEditor, HabitCueEditor, HabitDurationEditor, HabitParentEditor, HabitPriorityEditor,
  HabitRemindersEditor, HabitScheduleEditorPopover, HabitTitleEditor, HabitWhyEditor,
} from '../components/habits/inline/HabitInlineEditors'
import { HABIT_PRIORITY_OPTIONS } from '../utils/habitOptions'
import DreamGraphTabs from '../components/hierarchy/DreamGraphTabs'
import BlockersSection from '../components/blockers/BlockersSection'
import { OccurrencesSection } from '../components/occurrences/OccurrencesSection'
import { describeTaskCleanup } from '../utils/taskForm'
import { habitState, hasSchedule } from '../utils/habitSections'
import { scheduleWords, durationWords, reminderWords } from '../utils/habitWords'

/**
 * The habit page (Habits UX pass 2026-09-06), in the task page's frame:
 * breadcrumb → title with one summary line → the verbs → body | rail. Every
 * fact is edited where it is shown; the Add row offers what is not set yet;
 * "Done today" has an Undo; pause, resume, archive and restore are verbs, not
 * settings. The rail carries the task page's order — Fill the gaps · Plan ·
 * Hold me to it · Keeping up — so the person knows where to look.
 *
 * Owner rule 2026-09-06: a field is in the UI only when the person can do
 * something with it. The persona's classification of the habit (its "kind",
 * confidence, reason) and the formation stage stay in the backend for
 * analytics and never render here.
 */
const SECTION_HEADING = 'flex items-center gap-1.5 text-app-meta font-bold uppercase tracking-widest text-secondary'
const pad = (n: number) => String(n).padStart(2, '0')
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

const PAUSE_PRESETS: Array<{ id: PausePreset; label: string; hint: string }> = [
  { id: 'week', label: 'A week', hint: 'It comes back on its own' },
  { id: 'two_weeks', label: 'Two weeks', hint: 'It comes back on its own' },
  { id: 'month', label: 'A month', hint: 'It comes back on its own' },
  { id: 'open', label: 'Until I say', hint: 'Resume it from here whenever' },
]

export const HabitDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const [habit, setHabit] = useState<Habit | null>(null)
  const [card, setCard] = useState<HabitBoardCard | null>(null)
  const [journeyPlan, setJourneyPlan] = useState<HabitJourneyPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [pauseOpen, setPauseOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [journeyOpen, setJourneyOpen] = useState(true)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const habitService = useMemo(() => new HabitService(), [])
  const habitNumericId = id ? parseInt(id, 10) : NaN
  const checkin = useHabitAdjustmentCheckinForHabit(typeof habit?.habit_definition_id === 'number' ? habit.habit_definition_id : undefined)

  useProvideFeedbackContext({ pageType: 'habit_detail', entityType: 'habit', entityId: id })

  const loadCard = useCallback(async () => {
    if (!id) return
    try {
      const board = await habitService.getBoard(todayKey())
      setCard(board.habits[String(id)] || null)
    } catch { setCard(null) }
  }, [habitService, id])

  const load = useCallback(async () => {
    if (!id) return
    try {
      setError(null)
      const data = await habitService.getHabit(id)
      setHabit(data)
      void loadCard()
      habitService.getJourneyPlan(id).then(setJourneyPlan).catch(() => setJourneyPlan(null))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load habit')
    } finally {
      setLoading(false)
    }
  }, [habitService, id, loadCard])

  useEffect(() => { void load() }, [load])

  const onChanged = (h: Habit) => { setHabit(h); void loadCard() }

  // ── verbs ──
  const markToday = async (outcome: 'done' | 'skipped' | 'pending') => {
    if (!habit) return
    setBusy(outcome)
    try {
      const r = await habitService.markToday(habit.id, outcome, todayKey())
      if (r.board) setCard(r.board)
      if (outcome === 'done') toast.toast({ variant: 'success', title: 'Done today', description: habit.title, action: { label: 'Undo', onClick: () => void markToday('pending') } })
      else if (outcome === 'skipped') toast.toast({ variant: 'info', title: 'Not today', description: 'That is allowed. It does not break your run.', action: { label: 'Undo', onClick: () => void markToday('pending') } })
    } catch (err) {
      toast.error('Could not record that', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setBusy(null)
    }
  }

  const lifecycle = async (what: 'pause' | 'resume' | 'archive' | 'restore', preset?: PausePreset) => {
    if (!habit) return
    const defId = habit.habit_definition_id
    setBusy(what)
    try {
      if (what === 'restore') await habitLifecycle.restore(habit.id)
      else {
        if (typeof defId !== 'number') throw new Error('This habit has no schedule plane yet')
        if (what === 'pause') await habitLifecycle.pause(defId, preset || 'week')
        if (what === 'resume') await habitLifecycle.resume(defId)
        if (what === 'archive') await habitLifecycle.archive(defId)
      }
      setPauseOpen(false); setArchiveOpen(false)
      await load()
      if (what === 'pause') toast.toast({ variant: 'info', title: 'Paused', description: preset === 'open' ? 'It waits here until you resume it.' : 'It comes back on its own. Nothing will nag you meanwhile.' })
      if (what === 'resume') toast.success('Back on', 'The schedule refills from today.')
      if (what === 'archive') toast.toast({ variant: 'info', title: 'Archived', description: 'It stays in your history. Restore it any time.' })
      if (what === 'restore') toast.success('Restored', 'The schedule refills from today.')
    } catch (err) {
      toast.error('That did not go through', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setBusy(null)
    }
  }

  const confirmDelete = async () => {
    if (!habit) return
    try {
      const result = await habitService.deleteHabit(String(habit.id))
      setDeleteOpen(false)
      toast.toast({ variant: 'success', title: 'Habit deleted', description: describeTaskCleanup(result.cleanup) || undefined })
      navigate('/habits')
    } catch (err) {
      setDeleteOpen(false)
      toast.error('Could not delete it', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  // The persona's help that lands in the steps list.
  const breakdown = async (first: boolean) => {
    if (!habit || Number.isNaN(habitNumericId)) return
    setAiLoading(true); setAiError(null)
    try {
      if (first) await habitService.getFirstStep(habitNumericId); else await habitService.breakdownHabit(habitNumericId)
      await load()
      toast.success(first ? 'First step added' : 'Steps added', 'They are in the steps list; edit them as you like.')
    } catch (err) { setAiError(err instanceof Error ? err.message : 'Could not do that just now') }
    finally { setAiLoading(false) }
  }

  // ── frame ──
  if (loading) {
    return <div className="flex h-64 items-center justify-center"><RefreshCw className="h-6 w-6 animate-spin text-brand" /><span className="ml-3 text-secondary">Loading habit…</span></div>
  }
  if (error || !habit) {
    return (
      <div className="space-y-4">
        <Link to="/habits" className="inline-flex items-center gap-1 text-app-meta text-muted hover:text-brand"><ArrowLeft className="h-3.5 w-3.5" /> Habits</Link>
        <Card className="border-bad/30"><CardContent className="pt-6"><div className="py-8 text-center" role="alert">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-bad" />
          <h2 className="mb-2 text-xl font-semibold text-primary">{error || 'This habit is not here'}</h2>
          <Button onClick={() => navigate('/habits')}>Go to habits</Button>
        </div></CardContent></Card>
      </div>
    )
  }

  const state = habitState(habit)
  const archived = state.key === 'archived'
  const resting = state.key === 'paused' || state.key === 'parked'
  const live = !archived && !resting
  // Today's row only means something while the habit is live — a parked or
  // paused habit's page said "today at …" while its list row did not (2026-09-12).
  const todayOutcome = live ? card?.today?.outcome || null : null
  const dueToday = live && Boolean(card?.today) && todayOutcome === 'pending'
  const doneToday = todayOutcome === 'done'
  const skippedToday = todayOutcome === 'skipped'
  const stateDot = archived ? 'bg-soft' : resting ? 'bg-muted' : doneToday ? 'bg-ok' : 'bg-brand'
  const priorityWord = HABIT_PRIORITY_OPTIONS.find((o) => o.value === habit.priority)?.label
  const scheduled = hasSchedule(habit)

  type Fact = { key: string; label: string; value: React.ReactNode }
  const facts: Fact[] = []
  if (habit.minimum_viable_repetition) facts.push({ key: 'min', label: 'Bare minimum', value: live ? <HabitBareMinimumEditor habit={habit} onChanged={onChanged} /> : habit.minimum_viable_repetition })
  if (habit.cue_or_anchor) facts.push({ key: 'cue', label: 'Cue', value: live ? <HabitCueEditor habit={habit} onChanged={onChanged} /> : habit.cue_or_anchor })
  if (habit.parent?.title) facts.push({ key: 'parent', label: 'Belongs to', value: <span className="inline-flex items-center gap-1"><Link to={`/${habit.parent.type}s/${habit.parent.id}`} className="hover:text-brand hover:underline">{habit.parent.title}</Link>{live && <HabitParentEditor habit={habit} onChanged={onChanged} variant="icon" trigger={<span aria-label="Change" className="text-app-micro">✎</span>} />}</span> })
  if (habit.priority === 'high') facts.push({ key: 'priority', label: 'Priority', value: live ? <HabitPriorityEditor habit={habit} onChanged={onChanged} /> : priorityWord })

  const menuItems = [
    ...(live && (dueToday || !card?.today) ? [{ label: 'Not today', icon: <SkipForward className="h-4 w-4" />, onSelect: () => void markToday('skipped'), testId: 'habit-not-today' }] : []),
    ...(live ? [{ label: 'Just the first step', icon: <Layers className="h-4 w-4" />, onSelect: () => void breakdown(true), testId: 'habit-first-step' }] : []),
    ...(!archived ? [{ label: 'Archive', icon: <Archive className="h-4 w-4" />, onSelect: () => setArchiveOpen(true), testId: 'habit-archive' }] : []),
    { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => setDeleteOpen(true), danger: true, testId: 'habit-delete' },
  ]

  return (
    <div className="space-y-4">
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-app-meta text-muted" data-testid="habit-breadcrumb">
        <Link to="/habits" className="inline-flex shrink-0 items-center gap-1 hover:text-brand"><ArrowLeft className="h-3.5 w-3.5" /> Habits</Link>
        {habit.parent?.title && (<><span aria-hidden="true">/</span><Link to={`/${habit.parent.type}s/${habit.parent.id}`} className="truncate hover:text-brand" data-testid="habit-parent-link">{habit.parent.title}</Link></>)}
      </nav>

      {checkin.checkin && (
        <HabitAdjustmentCheckinCard checkin={checkin.checkin} markShown={checkin.markShown} respond={checkin.respond} diagnose={checkin.diagnose}
                                    chooseAction={checkin.chooseAction} confirm={checkin.confirm} pause={checkin.pause} onDone={() => { void checkin.refetch(); void load() }} />
      )}

      <Card>
        <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <CardTitle className={`text-xl leading-tight sm:text-2xl ${archived ? 'text-muted' : 'text-primary'}`}>
              {live ? <HabitTitleEditor habit={habit} onChanged={onChanged} trigger={habit.title} /> : habit.title}
            </CardTitle>
            <div className="inline-flex flex-wrap items-center gap-x-1.5 text-app-meta text-secondary" data-testid="habit-summary-line">
              <span className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${stateDot}`} aria-hidden="true" />{state.words || (doneToday ? 'Done today' : dueToday ? 'Due today' : 'Active')}</span>
              {state.key === 'paused' && state.pausedUntil && <span>until <Timestamp value={state.pausedUntil} mode="date" /></span>}
              {scheduled && <span>· {live ? <HabitScheduleEditorPopover habit={habit} onChanged={onChanged} trigger={scheduleWords(habit) || 'schedule'} /> : scheduleWords(habit)}</span>}
              {habit.duration_minutes ? <span>· {durationWords(habit.duration_minutes)}</span> : null}
              <span>· <LifeAreasPicker value={(habit.life_areas && habit.life_areas.length ? habit.life_areas : habit.life_area ? [habit.life_area] : [])} editable={live}
                                       onSave={async (values) => { await habitService.updateHabit(String(habit.id), { life_areas: values }); onChanged(await habitService.getHabit(String(habit.id))) }} testId="habit-life-area" /></span>
              {card && card.streak >= 2 && <span className="text-ok">· {card.streak} in a row</span>}
              {dueToday && card?.today?.scheduled_at && <span>· today at <Timestamp value={card.today.scheduled_at} mode="time" /></span>}
              {doneToday && card?.today?.at && <span className="text-ok">· done <Timestamp value={card.today.at} mode="time" /></span>}
              {skippedToday && <span>· not today</span>}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="habit-actions">
            {/* North Star rule 3 (2026-09-12): the page's one filled button is the
                next move — Done today while it is live and not yet done, Resume
                while paused, Restore once archived. Everything else is an outline. */}
            {live && !doneToday && (
              <Button size="xs" disabled={busy === 'done'} onClick={() => void markToday('done')} data-testid="habit-done-today">
                <CheckCircle2 className="mr-2 h-4 w-4" />{card?.today || !scheduled ? 'Done today' : 'Did it today'}
              </Button>
            )}
            {live && doneToday && (
              <Button variant="outline" size="xs" className="hover:bg-brand-subtle" disabled={busy === 'pending'} onClick={() => void markToday('pending')} data-testid="habit-undo-today">
                <Undo2 className="mr-2 h-4 w-4" />Done today · Undo
              </Button>
            )}
            {live && (
              <span className="relative">
                <Button variant="outline" size="xs" className="hover:bg-brand-subtle" aria-haspopup="menu" aria-expanded={pauseOpen} onClick={() => setPauseOpen((o) => !o)} data-testid="habit-pause">
                  <Pause className="mr-2 h-4 w-4" />Pause
                </Button>
                {pauseOpen && (
                  <div role="menu" aria-label="Pause for" className="absolute left-0 top-full z-30 mt-1 w-64 rounded-lg border border-soft bg-surface p-1 shadow-lg" data-testid="habit-pause-menu">
                    <p className="px-3 pb-1 pt-2 text-app-micro text-muted">A pause is not a miss. Nothing nags you meanwhile.</p>
                    {PAUSE_PRESETS.map((p) => (
                      <button key={p.id} role="menuitem" type="button" disabled={busy === 'pause'} onClick={() => void lifecycle('pause', p.id)} className="flex w-full items-start gap-3 rounded-md px-3 py-2 text-left hover:bg-surface-alt disabled:opacity-60" data-testid={`habit-pause-${p.id}`}>
                        <span className="min-w-0"><span className="block text-sm font-medium text-primary">{p.label}</span><span className="block text-xs text-muted">{p.hint}</span></span>
                      </button>
                    ))}
                  </div>
                )}
              </span>
            )}
            {state.key === 'paused' && (
              <Button size="xs" disabled={busy === 'resume'} onClick={() => void lifecycle('resume')} data-testid="habit-resume">
                <Play className="mr-2 h-4 w-4" />Resume
              </Button>
            )}
            {archived && (
              <Button size="xs" disabled={busy === 'restore'} onClick={() => void lifecycle('restore')} data-testid="habit-restore">
                <ArchiveRestore className="mr-2 h-4 w-4" />Restore
              </Button>
            )}
            <ActionMenu label={`More for ${habit.title}`} items={menuItems} testId="habit-more" />
          </div>
          {aiError && <div className="mt-2 flex items-center gap-2 text-sm text-bad"><AlertCircle className="h-4 w-4 shrink-0" /><span>{aiError}</span></div>}
          {aiLoading && <p className="mt-2 text-app-meta text-muted">Working on it…</p>}
        </CardHeader>

        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <div className="grid gap-6 border-t border-soft pt-4 lg:grid-cols-5">
            <div className="min-w-0 space-y-5 lg:col-span-3" data-testid="habit-body">
              <section data-testid="habit-about">
                <h3 className={SECTION_HEADING}><AlignLeft className="h-3.5 w-3.5 text-brand" /> About</h3>
                <p className="mt-2 text-sm text-primary" data-testid="habit-why">
                  {live ? <HabitWhyEditor habit={habit} onChanged={onChanged} trigger={habit.description || undefined} /> : (habit.description || <span className="text-muted">Nothing written yet.</span>)}
                </p>
                {facts.length > 0 && (
                  <dl className="mt-3 text-sm" data-testid="habit-facts">
                    {facts.map((f) => (
                      <div key={f.key} className="flex gap-3 py-1" data-testid={`habit-fact-${f.key}`}>
                        <dt className="w-28 shrink-0 pt-0.5 text-app-meta text-muted">{f.label}</dt>
                        <dd className="min-w-0 break-words text-primary">{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {live && <div className="mt-3"><HabitAddRow habit={habit} onChanged={onChanged} /></div>}
              </section>

              <HabitSteps habit={habit} card={card} todayKey={todayKey()} onChanged={onChanged} onCard={setCard} live={live} onBreakdown={() => breakdown(false)} breakdownBusy={aiLoading} onAllDone={() => void markToday('done')} />

              {journeyPlan && (
                <section data-testid="habit-journey">
                  <button type="button" onClick={() => setJourneyOpen((o) => !o)} aria-expanded={journeyOpen} className="flex w-full items-center gap-1.5 text-left">
                    {journeyOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted" /> : <ChevronRight className="h-3.5 w-3.5 text-muted" />}
                    <h3 className={SECTION_HEADING}><Route className="h-3.5 w-3.5 text-brand" /> Step by step</h3>
                    <span className="text-app-micro text-muted">{journeyPlan.steps.filter((s) => s.status === 'completed').length} of {journeyPlan.steps.length}</span>
                  </button>
                  {journeyOpen && (
                    <ol className="mt-2 space-y-1.5">
                      {journeyPlan.steps.map((step, idx) => {
                        const active = step.status === 'unlocked' && step.task_id != null
                        const done = step.status === 'completed'
                        return (
                          <li key={step.step_id} className="flex items-start gap-2 text-sm">
                            <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-app-micro font-bold ${done ? 'bg-ok text-on-brand' : active ? 'bg-brand text-on-brand' : 'bg-surface-alt text-muted'}`}>{done ? <CheckCircle2 className="h-3 w-3" /> : step.status === 'locked' ? <Lock className="h-3 w-3" /> : idx + 1}</span>
                            <span className="min-w-0">
                              <span className={done || active ? 'text-primary' : 'text-muted'}>{step.title}</span>
                              {active && step.task_id && <Link to={`/tasks/${step.task_id}`} className="ml-2 inline-flex items-center gap-1 text-app-micro text-brand hover:underline"><ExternalLink className="h-3 w-3" /> open</Link>}
                              {done && step.completed_at && <span className="ml-2 text-app-micro text-muted">done <Timestamp value={step.completed_at} mode="date" /></span>}
                            </span>
                          </li>
                        )
                      })}
                    </ol>
                  )}
                </section>
              )}

              <OccurrencesSection itemType="habit" itemId={Number(habit.id)} refreshKey={`${habit.updated_at || ''}:${card?.today?.outcome || ''}:${card?.streak ?? ''}:${(card?.today?.steps_done || []).join(',')}`} headingClass={SECTION_HEADING}
                                  steps={(habit.steps || []).map((s) => ({ id: s.id, text: s.text }))} onWrote={() => void loadCard()} logOnly />

              {!Number.isNaN(habitNumericId) && <BlockersSection itemType="habit" itemId={habitNumericId} className="rounded-xl border border-soft bg-surface-alt p-4" />}
            </div>

            <aside className="min-w-0 space-y-4 rounded-xl bg-surface-alt p-4 lg:col-span-2" data-testid="habit-rail">
              {live && <HabitGapsCard habit={habit} onChanged={onChanged} embedded />}

              <div className={live ? 'border-t border-soft pt-4' : ''} data-testid="habit-plan">
                <h3 className={SECTION_HEADING}><Calendar className="h-3.5 w-3.5 text-brand" /> Plan</h3>
                <div className="mt-1.5 space-y-1 text-sm text-primary">
                  <div data-testid="habit-plan-when">
                    {scheduled
                      ? (live ? <HabitScheduleEditorPopover habit={habit} onChanged={onChanged} trigger={scheduleWords(habit) || 'schedule'} /> : scheduleWords(habit))
                      : (live ? <HabitScheduleEditorPopover habit={habit} onChanged={onChanged} trigger="Not on a schedule yet · set one" /> : <span className="text-secondary">Never had a schedule</span>)}
                  </div>
                  {(habit.duration_minutes || live) && (
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 shrink-0 text-muted" />
                      <span>{live ? <HabitDurationEditor habit={habit} onChanged={onChanged} trigger={habit.duration_minutes ? `Takes ${durationWords(habit.duration_minutes)}` : 'How long each time?'} /> : `Takes ${durationWords(habit.duration_minutes)}`}</span>
                    </div>
                  )}
                  {card?.next && !card.today && <div className="text-secondary" data-testid="habit-plan-next">Next <Timestamp value={card.next.at || `${card.next.date}T12:00:00`} mode={card.next.at ? 'datetime' : 'date'} /></div>}
                  {dueToday && card?.today?.scheduled_at && <div className="text-secondary" data-testid="habit-plan-today">Today at <Timestamp value={card.today.scheduled_at} mode="time" /></div>}
                </div>
              </div>

              {/* Only while live: a paused or archived habit claimed "Reminds up to 4
                  times…" while nothing reminded (2026-09-12). */}
              {scheduled && live && (
                <div className="border-t border-soft pt-4" data-testid="habit-reminders">
                  <h3 className={SECTION_HEADING}><Bell className="h-3.5 w-3.5 text-brand" /> Hold me to it</h3>
                  <p className="mt-1.5 text-sm text-primary">{live ? <HabitRemindersEditor habit={habit} onChanged={onChanged} /> : reminderWords(habit)}</p>
                </div>
              )}

              <div className="border-t border-soft pt-4">
                <HabitKeepingUp card={card} headingClass={SECTION_HEADING} />
              </div>
              <p className="text-app-micro text-muted">Added <Timestamp value={habit.created_at} mode="date" />{habit.updated_at ? <> · changed <Timestamp value={habit.updated_at} mode="datetime" /></> : null}</p>
            </aside>
          </div>
        </CardContent>
      </Card>

      {habit?.id && <DreamGraphTabs nodeId={Number(habit.id)} nodeType="habit" />}

      <ConfirmationModal isOpen={archiveOpen} onClose={() => setArchiveOpen(false)} onConfirm={() => void lifecycle('archive')} title="Archive this habit?"
                         message={`“${habit.title}” leaves your list and stops coming round. It stays in your history, and you can restore it any time.`} variant="warning" confirmText="Archive" />
      <ConfirmationModal isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={confirmDelete} title="Delete this habit?"
                         message={`“${habit.title}” will be removed, along with its schedule and anything set to remind you about it. Archiving keeps the history instead.`} variant="error" confirmText="Delete" />
    </div>
  )
}

export default HabitDetail
